import {
  FormManager,
  type Schema,
  type SchemaKt,
  type ValidationMode,
} from "@ostack.tech/kform";
import { useEffect, useMemo, useRef } from "react";

import { equals } from "../utils/equals.ts";
import { usePrevious } from "../utils/usePrevious.ts";

/**
 * Hook that creates a new form manager given a {@link schema} and [external
 * contexts]{@link externalContexts}.
 *
 * Note that a new instance of a form manager will be created every time
 * {@link schema} changes; the external contexts and validation mode, however,
 * may change over time and the hook will simply update the external contexts of
 * the current form manager.
 *
 * @param schema Schema of the form, used to create a new instance of a form
 *   manager.
 * @param initialValue Initial form value.
 * @param externalContexts External contexts available to validations of
 *   {@link schema}.
 * @param validationMode Validation mode (automatic or manual validation).
 * @returns New form manager.
 */
export function useNewFormManager(
  schema: Schema | SchemaKt,
  initialValue?: unknown,
  externalContexts?: Record<string, unknown>,
  validationMode: ValidationMode = "auto",
): FormManager {
  const initialValueRef = useRef(initialValue);
  const formManager = useMemo(
    () =>
      new FormManager(
        schema,
        initialValueRef.current,
        undefined,
        undefined,
        false,
      ),
    [schema],
  );
  useEffect(() => {
    void formManager.init(externalContexts, validationMode);
    return () => void formManager.destroy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formManager]);
  const prevFormManager = usePrevious(formManager);

  // Manage external contexts without recreating the form manager
  const prevExternalContexts = usePrevious(externalContexts);
  useEffect(() => {
    if (formManager === prevFormManager) {
      // Add new external contexts
      if (externalContexts) {
        for (const contextName of Object.keys(externalContexts)) {
          const externalContext = externalContexts[contextName];
          if (
            externalContext !== undefined &&
            (!prevExternalContexts ||
              !equals(externalContext, prevExternalContexts[contextName]))
          ) {
            void formManager.setExternalContext(contextName, externalContext);
          }
        }
      }
      // Remove old (no longer provided) external contexts
      if (prevExternalContexts) {
        for (const contextName of Object.keys(prevExternalContexts)) {
          if (
            prevExternalContexts[contextName] !== undefined &&
            externalContexts?.[contextName] === undefined
          ) {
            void formManager.removeExternalContext(contextName);
          }
        }
      }
    }
  }, [externalContexts, formManager, prevExternalContexts, prevFormManager]);

  // Manage validation mode without recreating the form manager
  const prevValidationMode = usePrevious(validationMode);
  useEffect(() => {
    if (
      formManager === prevFormManager &&
      validationMode !== prevValidationMode
    ) {
      void formManager.setValidationMode(validationMode);
    }
  }, [formManager, validationMode, prevValidationMode, prevFormManager]);

  return formManager;
}
